import { z } from "zod/v4";

import type { ShopifyGqlIdType } from "../dtos/shopify-product-variants";
import { toShopifyGqlIdIfBare } from "./shopify-gid-primitive";

export { toShopifyGqlId, toShopifyGqlIdIfBare } from "./shopify-gid-primitive";

/**
 * A string field whose VALUE is a Shopify GID of `type`, promoting the bare
 * numeric id the Shopify admin shows and refusing anything that is neither.
 *
 * `"123456789"` and `"not-a-thing"` used to produce byte-identical errors,
 * though the first was one prefix from valid. The first no longer errors at
 * all; the message the second gets names the value that was received, because
 * an author staring at a list of ids needs to know WHICH one the platform
 * could not read.
 *
 * The promotion is idempotent, so a boundary that already normalized (the
 * surfaces lane's `normalize-request.ts`) and this schema can both run over
 * the same value without double-prefixing.
 */
export function shopifyGidSchema(type: ShopifyGqlIdType, label: string) {
  const canonical = new RegExp(`^gid://shopify/${type}/[1-9]\\d*$`);
  return z
    .string()
    .superRefine((id, ctx) => {
      if (!canonical.test(toShopifyGqlIdIfBare(id, type))) {
        ctx.addIssue({
          code: "custom",
          message:
            `received "${id}" — must be a Shopify ${label} GID ` +
            `(gid://shopify/${type}/<n>) or the bare numeric id the Shopify admin shows`,
        });
      }
    })
    .transform((id) => toShopifyGqlIdIfBare(id, type));
}

export const ShopifyProductGidSchema = shopifyGidSchema("Product", "product");

export const ShopifyVariantGidSchema = shopifyGidSchema(
  "ProductVariant",
  "variant"
);

export const ShopifyCollectionGidSchema = shopifyGidSchema(
  "Collection",
  "collection"
);
